export type Volume = {
  numeral: string;
  title: string;
  tagline: string;
  intro: string;
  listLabel: string;
  items: string[];
  ordered?: boolean;
  outcome: string;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The Mastopexy Case Assessment Framework",
    tagline: "Know what to review before you think about the procedure.",
    intro:
      "I created this volume to help you organize the educational review of a mastopexy case before moving into the operative sequence.",
    listLabel: "You'll work through:",
    items: [
      "Patient profile",
      "Primary aesthetic objectives",
      "Breast characteristics",
      "Degree and pattern of ptosis",
      "Skin and tissue considerations",
      "Breast asymmetry",
      "Relevant measurements",
      "Previous procedures",
      "Patient expectations",
      "Questions requiring further review",
      "Documentation prompts",
      "Preoperative planning considerations",
    ],
    outcome: "Turn a complex case into an organized case summary.",
  },
  {
    numeral: "II",
    title: "The Mastopexy Planning Map",
    tagline: "Organize the variables that influence your surgical planning.",
    intro:
      "Instead of looking at the breast as one problem, this volume gives you a structured educational map for reviewing the different elements of the case.",
    listLabel: "You'll review:",
    items: [
      "Breast shape",
      "Breast volume",
      "Tissue characteristics",
      "Skin envelope",
      "Nipple-areola considerations",
      "Symmetry",
      "Patient goals",
      "Relevant anatomical observations",
      "Planning questions",
      "Case-specific considerations",
    ],
    outcome: "A clearer way to organize your preoperative thinking.",
  },
  {
    numeral: "III",
    title: "The Mastopexy Procedure Blueprint",
    tagline:
      "Follow the complete educational procedure sequence from preparation through postoperative review.",
    intro:
      "This is the core of the system. Instead of presenting isolated technical concepts, I organize the educational material into a logical procedural sequence.",
    listLabel: "You'll review:",
    items: [
      "Case preparation",
      "Preoperative planning",
      "Marking concepts",
      "Operating-room preparation",
      "Procedural sequence",
      "Key technical checkpoints",
      "Intraoperative considerations",
      "Symmetry review",
      "Closure and finishing considerations",
      "Immediate postoperative review",
    ],
    outcome:
      "An educational framework — not a substitute for supervised surgical training, institutional protocols or individualized clinical judgment.",
  },
  {
    numeral: "IV",
    title: "The Mastopexy Case Atlas",
    tagline: "Study cases instead of simply reading theory.",
    intro:
      "This is where the system becomes active. You'll receive educational mastopexy cases designed to help you practice the framework.",
    listLabel: "For each case:",
    ordered: true,
    items: [
      "Review the case",
      "Identify the relevant variables",
      "Define your priorities",
      "Review the planning considerations",
      "Work through the educational procedure sequence",
      "Compare your reasoning with the educational review",
      "Record what you learned",
    ],
    outcome:
      "Instead of passively consuming another lecture, you're actively practicing case-based reasoning.",
  },
  {
    numeral: "V",
    title: "The Mastopexy Complication & Decision Review",
    tagline: "Study difficult scenarios before you encounter them in your learning.",
    intro:
      "This volume focuses on educational review of situations that deserve additional attention.",
    listLabel: "For each scenario:",
    items: [
      "What happened?",
      "What information should be reviewed?",
      "Which factors may have contributed?",
      "What questions should be asked?",
      "What could have been considered during planning?",
      "What should be documented?",
      "What would you review differently?",
      "What is the key educational lesson?",
    ],
    outcome:
      "The goal isn't to memorize complications. It's to train yourself to recognize the variables that deserve attention.",
  },
  {
    numeral: "VI",
    title: "The Surgeon's Mastopexy Case Review System",
    tagline: "Turn every case into another opportunity to learn.",
    intro: "This volume gives you the tools to build your own educational case archive.",
    listLabel: "You'll receive templates for:",
    items: [
      "Case documentation",
      "Preoperative review",
      "Procedure review",
      "Case reflection",
      "Case comparison",
      "Learning points",
      "Questions for future review",
      "Knowledge gaps",
      "Monthly case review",
      "Progress tracking",
      "Annual professional reflection",
    ],
    outcome:
      "Every case becomes another opportunity to improve the way you study and organize your surgical thinking.",
  },
];
